// A swatch grid for a REAL Shopify option axis, rendered inside a garment
// stage (see Stage.variantAxis in ../stages.ts).
//
// WHY THIS IS NOT A CustomField. The cap and sock colours are pure metadata:
// the gift box already spends all three Shopify option slots (Hoodie size,
// Hoodie colour, Sock size), so those two ride to the packer as line-item
// properties and cannot affect price or stock. Hoodie colour is different, it
// IS option2 of the 72-variant grid. If the customiser owned it as its own
// value, everything downstream that reads the variant (price, availability,
// the sold-out pre-order path, the on-garment preview tint, analytics) would
// disagree with what the customer picked.
//
// So this component does not own the value at all. It DERIVES its selection
// from the currently selected variant, and a tap drives the theme's own
// option radio, exactly as _rk-sg-theme/snippets/rk-size-helper.liquid already
// does for size. The theme resolves the new variant, rewrites ?variant= via
// replaceState, and the customiser re-mounts with fresh context. The theme
// stays the single source of truth and nothing can drift.
//
// SOLD-OUT COLOURS STAY SELECTABLE, deliberately. On customiser products the
// theme never applies its `rk-soldout` treatment: since 2026-07-03 a sold-out
// combination is a pre-order at 15% off, and the picker marks it `rk-preorder`
// instead. Greying an unavailable colour out here would silently delete that
// revenue path while looking like careful defensive coding.

import * as React from "react";
import type { ProductJsonShape } from "../api";

/** Find and click the theme's radio for `axisName` = `value`.
 *  Returns false when the control is not on the page (e.g. the theme markup
 *  changed), so the caller can decide whether to surface anything. */
export function pickThemeOption(axisName: string, value: string): boolean {
  if (typeof document === "undefined") return false;
  const radios = Array.from(
    document.querySelectorAll<HTMLInputElement>(
      'input[type="radio"][data-single-option-selector]',
    ),
  );
  const wanted = axisName.trim().toLowerCase();
  const target = radios.find((r) => {
    // name is `Section-<id>-options[Hoodie colour]`.
    const m = /options\[([^\]]+)\]/.exec(r.name || "");
    if (!m) return false;
    if (m[1].trim().toLowerCase() !== wanted) return false;
    return (r.value || "").trim() === value;
  });
  if (!target) return false;
  // .click() (not .checked = true) so Alpine's @change -> optionChange() runs
  // and the theme actually resolves the new variant.
  if (!target.checked) target.click();
  return true;
}

type Variant = NonNullable<ProductJsonShape["variants"]>[number];

/** Option values are read off the variant grid in first-seen order rather
 *  than from productJson.options, whose shape differs between Shopify
 *  versions (bare strings vs {name, values}). */
function axisIndex(productJson: ProductJsonShape, axisName: string): number {
  const names = (productJson.options ?? []).map((o) =>
    typeof o === "string" ? o : (o?.name ?? ""),
  );
  return names.findIndex(
    (n) => n.trim().toLowerCase() === axisName.trim().toLowerCase(),
  );
}

function optionAt(v: Variant, idx: number): string | null {
  const raw = [v.option1, v.option2, v.option3][idx];
  return raw == null ? null : String(raw);
}

export function VariantAxisPicker({
  axisName,
  label,
  productJson,
  variantColours,
  currentVariantId,
}: {
  axisName: string;
  /** Shown above the grid. Falls back to the axis name. */
  label?: string;
  productJson: ProductJsonShape | null;
  variantColours: Record<string, string> | undefined;
  currentVariantId: string | null;
}) {
  const idx = productJson ? axisIndex(productJson, axisName) : -1;
  const variants = productJson?.variants ?? [];

  const current = React.useMemo(
    () =>
      variants.find((v) => String(v.id) === String(currentVariantId)) ?? null,
    [variants, currentVariantId],
  );

  const options = React.useMemo(() => {
    if (idx < 0) return [];
    const seen = new Map<string, { value: string; variant: Variant }>();
    for (const v of variants) {
      const value = optionAt(v, idx);
      if (!value) continue;
      const existing = seen.get(value);
      // Prefer the variant that keeps every OTHER axis on what the customer
      // already chose, so tapping a colour cannot silently change their size.
      const keepsOthers =
        !!current &&
        [0, 1, 2].every(
          (i) => i === idx || optionAt(v, i) === optionAt(current, i),
        );
      if (!existing || keepsOthers) seen.set(value, { value, variant: v });
    }
    return Array.from(seen.values());
  }, [variants, idx, current]);

  if (idx < 0 || options.length < 2) return null;
  const selected = current ? optionAt(current, idx) : null;

  return (
    <div
      className="rk-pc-field rk-pc-field--swatch"
      data-rk-variant-axis={axisName}
    >
      <label className="rk-pc-field__label">{label ?? axisName}</label>
      <div
        className="rk-pc-field__swatch-grid"
        role="radiogroup"
        aria-label={label ?? axisName}
      >
        {options.map((opt) => {
          const isSelected = selected === opt.value;
          const hex = variantColours?.[String(opt.variant.id)];
          return (
            <button
              key={opt.value}
              type="button"
              className={`rk-pc-field__swatch${isSelected ? " is-selected" : ""}`}
              onClick={() => pickThemeOption(axisName, opt.value)}
              aria-pressed={isSelected}
              aria-label={opt.value}
              title={opt.value}
            >
              <span
                className="rk-pc-field__swatch-dot"
                style={{ background: hex || "#e5e7eb" }}
              />
              <span className="rk-pc-field__swatch-label">{opt.value}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
